import { ProductDetails, Product, Variant } from '@services';
import { Cart, LineItem } from '@services/cart';
import { OrderExpanded } from '@services/checkout';

export const EVENT_SOURCE = {
  MERCHANT: 'EVENT_SOURCE_MERCHANT',
  AGENT: 'EVENT_SOURCE_AGENT'
} as const;

export interface ShoppingEventCredentials {
  token: string;
  sessionId: string;
}

let shoppingEventCredentials: ShoppingEventCredentials | null = null;

export const setShoppingEventCredentials = (
  credentials: ShoppingEventCredentials
): void => {
  shoppingEventCredentials = credentials;
};

export interface AnalyticsListContext {
  listId?: string;
  listName?: string;
}

const mapItem = (
  item: Product | ProductDetails | Variant | LineItem,
  quantity?: number,
  listContext?: AnalyticsListContext,
  index?: number
): { [key: string]: string | number | undefined } => ({
  item_id: item.id,
  item_name: item.name,
  discount: item.price?.discountedValue
    ? (item.price?.value ?? 0) - item.price.discountedValue
    : 0,
  item_category: item.productType?.name || item.productType,
  price: item.price?.discountedValue ?? item.price?.value ?? 0,
  ...(quantity ? { quantity } : {}),
  ...(listContext?.listId ? { item_list_id: listContext.listId } : {}),
  ...(listContext?.listName ? { item_list_name: listContext.listName } : {}),
  ...(index ? { index } : {})
});

const pushEvent = (event: string, ecommerce: object): void => {
  if (!window?.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ecommerce });
};

const postAgenticShoppingEvent = (
  eventType: string,
  eventData: object
): void => {
  if (!shoppingEventCredentials || !process.env.NEXT_PUBLIC_API_SHOPPING_EVENTS) return;
  const { token, sessionId } = shoppingEventCredentials;
  const projectId = process.env.NEXT_PUBLIC_SHOPPING_EVENTS_PROJECT_ID;
  const location = process.env.NEXT_PUBLIC_SHOPPING_EVENTS_LOCATION;

  const parent = `projects/${projectId}/locations/${location}/commerceSessions/${sessionId}`;
  const name = `projects/${projectId}/locations/${location}/commerceSessions/${sessionId}/shoppingEvents/${crypto.randomUUID()}`;

  fetch(`${process.env.NEXT_PUBLIC_API_SHOPPING_EVENTS}/${parent}/shoppingEvents`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'X-Goog-Session-Token': sessionId,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ [eventType]: eventData, name })
  }).catch(() => {});
};

export const pushViewItemListEvent = (
  products: Product[],
  listContext: AnalyticsListContext = {}
): void => {
  if (!products || products.length === 0) return;
  pushEvent('view_item_list', {
    item_list_id: listContext.listId,
    item_list_name: listContext.listName,
    items: products.map((product, index) =>
      mapItem(product, undefined, listContext, index)
    )
  });
};

export const pushSelectItemEvent = (
  product: Product,
  listContext: AnalyticsListContext = {}
): void => {
  if (!product) return;
  pushEvent('select_item', {
    item_list_id: listContext.listId,
    item_list_name: listContext.listName,
    items: [ mapItem(product, undefined, listContext) ]
  });
};

export const pushViewItemEvent = (product: ProductDetails): void => {
  if (!product) return;
  pushEvent('view_item', {
    currency: product.price.currencyCode,
    value: product.price.value,
    items: [ mapItem(product) ]
  });
};

export const pushAddToCartEvent = (
  product: Product | ProductDetails | Variant,
  quantity: number,
  source: string = EVENT_SOURCE.MERCHANT
): void => {
  if (!product) return;
  pushEvent('add_to_cart', {
    currency: product.price.currencyCode,
    value: (product.price.discountedValue || product.price.value) * quantity,
    items: [ mapItem(product, quantity) ]
  });
  postAgenticShoppingEvent('add_to_cart', {
    currency: product.price.currencyCode,
    value: (product.price.discountedValue || product.price.value) * quantity,
    source,
    items: [ mapItem(product, quantity) ]
  });
};

export const pushRemoveFromCartEvent = (
  lineItem: LineItem,
  source: string = EVENT_SOURCE.MERCHANT
): void => {
  if (!lineItem) return;
  const price = lineItem.price ?? lineItem.totalPrice;
  pushEvent('remove_from_cart', {
    currency: price.currencyCode,
    value:
      ((lineItem.price?.discountedValue ?? lineItem.price?.value ?? price.value) ?? 0) *
      lineItem.quantity,
    items: [ mapItem(lineItem, lineItem.quantity) ]
  });
  postAgenticShoppingEvent('remove_from_cart', {
    currency: lineItem.price.currencyCode,
    value: (lineItem.price.discountedValue || lineItem.price.value) * lineItem.quantity,
    source,
    items: [ mapItem(lineItem, lineItem.quantity) ]
  });
};

export const pushViewCartEvent = (cart: Cart): void => {
  if (!cart) return;
  pushEvent('view_cart', {
    currency: cart.totalPrice.currencyCode,
    value: cart.totalPrice.value,
    items: cart.lineItems.map(item => mapItem(item, item.quantity))
  });
};

export const pushBeginCheckoutEvent = (cart: Cart): void => {
  if (!cart) return;
  pushEvent('begin_checkout', {
    currency: cart.totalPrice.currencyCode,
    value: cart.totalPrice.value,
    coupon: cart.discountCodes?.map(code => code.discountCode.code).join(', '),
    items: cart.lineItems.map(item => mapItem(item, item.quantity))
  });
  postAgenticShoppingEvent('begin_checkout', {
    currency: cart.totalPrice.currencyCode,
    value: cart.totalPrice.value,
    source: EVENT_SOURCE.MERCHANT,
    items: cart.lineItems.map(item => mapItem(item, item.quantity))
  });
};

export const pushAddShippingInfoEvent = (
  cart: Cart,
  shippingMethod: string
): void => {
  if (!cart || !shippingMethod) return;
  pushEvent('add_shipping_info', {
    currency: cart.totalPrice.currencyCode,
    value: cart.totalPrice.value,
    shipping_tier: shippingMethod,
    coupon: cart.discountCodes?.map(code => code.discountCode.code).join(', '),
    items: cart.lineItems.map(item => mapItem(item, item.quantity))
  });
};

export const pushAddPaymentInfoEvent = (
  cart: Cart,
  paymentType: string
): void => {
  if (!cart || !paymentType) return;
  pushEvent('add_payment_info', {
    currency: cart.totalPrice.currencyCode,
    value: cart.totalPrice.value,
    payment_type: paymentType,
    coupon: cart.discountCodes?.map(code => code.discountCode.code).join(', '),
    items: cart.lineItems.map(item => mapItem(item, item.quantity))
  });
};

export const pushPurchaseEvent = (order: Cart | OrderExpanded): void => {
  if (!order) return;
  pushEvent('purchase', {
    transaction_id: order.id,
    value: order.totalPrice.value,
    shipping: order.shippingPrice?.value,
    currency: order.totalPrice.currencyCode,
    coupon: order.discountCodes?.map(code => code.discountCode.code).join(', '),
    items: order.lineItems.map(item => mapItem(item, item.quantity))
  });
  postAgenticShoppingEvent('purchase', {
    currency: order.totalPrice.currencyCode,
    value: order.totalPrice.value,
    transaction_id: order.id,
    source: EVENT_SOURCE.MERCHANT,
    items: order.lineItems.map(item => mapItem(item, item.quantity))
  });
};

export const pushViewPromotionEvent = (
  promotionName: string,
  promotionId?: string
): void => {
  if (!promotionId || !promotionName) return;
  pushEvent('view_promotion', {
    promotion_name: promotionName,
    ...(promotionId ? { promotion_id: promotionId } : {}),
    items: []
  });
};

export const pushSelectPromotionEvent = (
  promotionName: string,
  promotionId?: string
): void => {
  if (!promotionId || !promotionName) return;
  pushEvent('select_promotion', {
    promotion_name: promotionName,
    ...(promotionId ? { promotion_id: promotionId } : {}),
    items: []
  });
};
